import React from 'react';
import { t } from 'i18next';
import { BusTopBg, BusBottomBg, BusDoorIcon, FridgeIcon, SeatIcon, ToiletIcon } from '@/assets/icons/bus/_';
import { cloneDeep, range } from 'lodash';
import { BusEntityType, SeatingType } from '@/constants';
import './styles.less';
import BusMiddleBg from '@/assets/icons/bus/bus-middle-bg.png';
import { Button, Card, Col, Divider, Dropdown, Form, Input, InputNumber, Modal, Radio, Row, Select } from 'antd';
import { ModalWidth, ROW_GUTTER, VALIDATE_REQUIRED } from '@/config';
import { useTranslation } from 'react-i18next';
import { DndContext } from '@dnd-kit/core';
import DraggableEntity from './DraggableEntity';
import DroppableSeat from './DroppableSeat';
import { PlusOutlined, ReloadOutlined, SaveOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { CompanyTicketClassDto, CreateUpdateSeatingModelDetailModel } from '@/__generated';
type Props = {
  numberOfRow: number;
  type: number;
  ticketClasses: CompanyTicketClassDto[];
  originSeatsMap?: CreateUpdateSeatingModelDetailModel[];
  onChange: (seats: CreateUpdateSeatingModelDetailModel[]) => void;
};
const draggableList = [
  { key: 'Seat', get label() { return t('Seat'); }, icon: <SeatIcon /> },
  { key: 'ToiletRight', get label() { return t('Toilet right'); }, icon: <ToiletIcon /> },
  { key: 'ToiletLeft', get label() { return t('Toilet left'); }, icon: <ToiletIcon style={{ transform: 'rotateY(180deg)' }} /> },
  { key: 'Door', get label() { return t('Door'); }, icon: <BusDoorIcon /> },
  { key: 'Fridge', get label() { return t('Fridge'); }, icon: <FridgeIcon /> },
];

const EntityMapping = {
  Seat: BusEntityType.Seat,
  ToiletRight: BusEntityType.ToiletRight,
  ToiletLeft: BusEntityType.ToiletLeft,
  Door: BusEntityType.Door,
  Fridge: BusEntityType.Fridge,
};
const toDict = (arr: CreateUpdateSeatingModelDetailModel[]) => {
  let dict = {};
  arr.forEach(a => {
    dict[`${a.row}-${a.column}`] = a;
  });
  return dict;
};
const isCtrlKey = (event: MouseEvent) => event.ctrlKey || event.metaKey;
let onMoving = false;
let startSelectedItem: string | null | undefined = undefined;
let endSelectedItem: string | null | undefined = undefined;
interface ISeatDataSet {
  key: string;
  allowselect: any;
}

const GenerateNameConfig = {
  types: [
    { value: 1, get label() { return t('Only number (ex: 01, 02)'); } },
    { value: 2, get label() { return t('Character and number (ex: A01, A02)'); } },
    { value: 3, get label() { return t('Number and  character  (ex: 01A, 20A)'); } },
    { value: 4, get label() { return t('Ticket class and number (ex: E01, B02)'); } },
    { value: 5, get label() { return t('Number and ticket class   (ex: 01E, 40B)'); } },
  ],
  staringChar: ['A', 'B', 'C', 'D', 'E', 'F'].map(x => ({ label: x, value: x })),
};
export default function BusDesign({ numberOfRow, type, ticketClasses, originSeatsMap, onChange }: Props) {
  const { t } = useTranslation();
  const [generateNameForm] = Form.useForm();
  const [selectedItems, setSelectedItems] = React.useState<string[]>([]);
  const busRef = React.useRef<any>();
  const [seatMap, setSeatMap] = React.useState<{ [key: string]: CreateUpdateSeatingModelDetailModel }>(toDict(originSeatsMap || []));
  const [displayGenerateSeatNameModal, setDisplayGenerateSeatNameModal] = React.useState(false);
  const defaultTicketClass = React.useMemo(() => {
    return ticketClasses.find(x => x.isStandard);
  }, [ticketClasses]);
  React.useEffect(() => {
    setSeatMap(toDict(originSeatsMap || []));
  }, [originSeatsMap]);

  React.useEffect(() => {}, []);
  React.useEffect(() => {
    onChange(
      Object.keys(seatMap)
        .map(key => seatMap[key])
        .filter(x => x),
    );
  }, [seatMap]);

  const numberOfCol = React.useMemo(() => {
    if (type === SeatingType['2X2 (5)']) {
      return 5;
    } else if (type === SeatingType['3X3 (7)']) {
      return 7;
    }
    return 6;
  }, [type]);
  const removeSeat = (seatKey: string) => {
    setSeatMap(s => {
      delete s[seatKey];
      return { ...s };
    });
  };
  const onMoveEntity = (entityType: string, destination: string) => {
    let rowCol = destination.split('-').map(x => parseInt(x));
    if (rowCol.length === 2) {
      let obj: any = {
        busEntityType: EntityMapping[entityType as any],
        column: rowCol[1],
        row: rowCol[0],
      };
      if (obj.busEntityType === BusEntityType.Seat) {
        obj.baseTicketClassId = defaultTicketClass?.ticketClassId;
      }
      setSeatMap(seats => {
        return {
          ...seats,
          [destination]: obj,
        };
      });
    }
  };

  const renderSelectedCell = () => {
    const tableElement = document.getElementById('bus-model');
    if (!tableElement) {
      return;
    }
    tableElement.querySelectorAll('.bus-seat-item.selected').forEach(x => x.classList.remove('selected'));
    if (startSelectedItem && endSelectedItem) {
      let startItem = startSelectedItem.split('-').map(x => parseInt(x));
      let stopItem = endSelectedItem.split('-').map(x => parseInt(x));

      let fromRow = Math.min(startItem[0], stopItem[0]);
      let toRow = Math.max(startItem[0], stopItem[0]);

      let fromCol = Math.min(startItem[1], stopItem[1]);
      let toCol = Math.max(startItem[1], stopItem[1]);
      for (var row = fromRow; row <= toRow; row++) {
        for (var col = fromCol; col <= toCol; col++) {
          let key = `${row}-${col}`;
          if (!seatMap[key]) {
            tableElement.querySelector(`[data-key="${key}"]`)?.classList.add('selected');
          }
        }
      }
    }
  };
  React.useEffect(() => {
    const tableElement = document.getElementById('bus-model');
    if (!tableElement) {
      return;
    }
    tableElement.querySelectorAll('.bus-seat-item.selected').forEach(x => x.classList.remove('selected'));
    if (selectedItems?.length) {
      selectedItems.forEach(item => {
        tableElement.querySelector(`[data-key="${item}"]`)?.classList.add('selected');
      });
    }
  }, [selectedItems]);
  const handleMouseDown = (event: MouseEvent) => {
    if (!isCtrlKey(event)) {
      onMoving = true;
      setSelectedItems([]);

      const dataset: ISeatDataSet = (event.target as any)?.dataset;
      if (dataset?.key && dataset?.allowselect) {
        startSelectedItem = dataset.key;
      }
    }
  };

  const handleMouseMove = (event: MouseEvent) => {
    if (onMoving && startSelectedItem) {
      const dataset: ISeatDataSet = (event.target as any)?.dataset;
      if (dataset?.allowselect && dataset?.key) {
        endSelectedItem = dataset.key;
        renderSelectedCell();
      }
    }
  };

  const handleMouseUp = (event: MouseEvent) => {
    if (onMoving) {
      const tableElement = document.getElementById('bus-model');
      if (tableElement) {
        tableElement.querySelectorAll('.bus-seat-item.selected').forEach(x => x.classList.remove('selected'));
      }

      const dataset: ISeatDataSet = (event.target as any)?.dataset;
      if (dataset?.allowselect && dataset?.key && startSelectedItem) {
        endSelectedItem = dataset?.key;
        let startItem = startSelectedItem.split('-').map(x => parseInt(x));
        let stopItem = endSelectedItem.split('-').map(x => parseInt(x));

        let fromRow = Math.min(startItem[0], stopItem[0]);
        let toRow = Math.max(startItem[0], stopItem[0]);

        let fromCol = Math.min(startItem[1], stopItem[1]);
        let toCol = Math.max(startItem[1], stopItem[1]);
        let items: string[] = [];
        if (fromRow !== toRow || fromCol !== toCol) {
          for (var row = fromRow; row <= toRow; row++) {
            for (var col = fromCol; col <= toCol; col++) {
              let key = `${row}-${col}`;
              if (!seatMap[key]) {
                items.push(key);
              }
            }
          }
          setSelectedItems(items);
        }
      } else {
        setSelectedItems([]);
      }
      onMoving = false;
      startSelectedItem = null;
      endSelectedItem = null;
    }
  };
  const handleClick = (event: MouseEvent) => {
    if (isCtrlKey(event)) {
      const dataset: ISeatDataSet = (event.target as any)?.dataset;
      if (dataset?.allowselect && dataset?.key) {
        setSelectedItems(f => {
          if (f.some(x => x === dataset.key)) {
            return f.filter(x => x !== dataset.key);
          }
          return [...f, dataset.key];
        });
      }
    }
  };
  React.useEffect(() => {
    let tableElement;
    tableElement = document.getElementById('bus-model');
    if (tableElement) {
      tableElement.addEventListener('mousedown', handleMouseDown);
      tableElement.addEventListener('mousemove', handleMouseMove);
      tableElement.addEventListener('mouseup', handleMouseUp);
      tableElement.addEventListener('click', handleClick);
    }

    return () => {
      tableElement?.removeEventListener('mousedown', handleMouseDown);
      tableElement?.removeEventListener('mousemove', handleMouseMove);
      tableElement?.removeEventListener('mouseup', handleMouseUp);
      tableElement?.removeEventListener('click', handleClick);
    };
  }, [busRef, seatMap]);

  const addMultiSeat = (ticketId?: any) => {
    let seats: { [key: string]: CreateUpdateSeatingModelDetailModel } = {};
    selectedItems.forEach(s => {
      seats[s] = {
        row: parseInt(s.split('-')[0]),
        column: parseInt(s.split('-')[1]),
        busEntityType: BusEntityType.Seat as any,
        baseTicketClassId: ticketId || defaultTicketClass?.ticketClassId,
      };
    });
    setSeatMap(s => {
      return { ...s, ...seats };
    });
    setSelectedItems([]);
  };

  const setAllEmptySeatAsDefaultValue = () => {
    let clonedSeatMap = cloneDeep(seatMap);
    Object.keys(clonedSeatMap).forEach(key => {
      if (clonedSeatMap[key].busEntityType === BusEntityType.Seat && !clonedSeatMap[key].baseTicketClassId) {
        clonedSeatMap[key].baseTicketClassId = defaultTicketClass?.ticketClassId;
        clonedSeatMap[key].seatColor = defaultTicketClass?.seatColor;
      }
    });
    setSeatMap(clonedSeatMap);
  };
  const generateSeatName = (generateType: number, startingNumber: number, startingCharacter: string) => {
    let clonedSeatMap = cloneDeep(seatMap);
    if (generateType === 1) {
      let seatIndex = startingNumber;
      for (var rowIndex = 0; rowIndex < numberOfRow; rowIndex++) {
        for (var colIndex = 0; colIndex < numberOfCol; colIndex++) {
          let key = `${rowIndex}-${colIndex}`;
          if (clonedSeatMap[key]?.busEntityType === BusEntityType.Seat) {
            clonedSeatMap[key].entityName = `${seatIndex}`.padStart(2, '0');
            seatIndex++;
          }
        }
      }
    } else if (generateType === 2) {
      let seatIndex = startingNumber;
      for (var rowIndex = 0; rowIndex < numberOfRow; rowIndex++) {
        let rowChar = String.fromCharCode(startingCharacter.charCodeAt(0) + rowIndex);
        for (var colIndex = 0; colIndex < numberOfCol; colIndex++) {
          let key = `${rowIndex}-${colIndex}`;
          if (clonedSeatMap[key]?.busEntityType === BusEntityType.Seat) {
            clonedSeatMap[key].entityName = rowChar + `${seatIndex}`.padStart(2, '0');
            seatIndex++;
          }
        }
      }
    } else if (generateType === 3) {
      let seatIndex = startingNumber;
      for (var rowIndex = 0; rowIndex < numberOfRow; rowIndex++) {
        let rowChar = String.fromCharCode(startingCharacter.charCodeAt(0) + rowIndex);
        for (var colIndex = 0; colIndex < numberOfCol; colIndex++) {
          let key = `${rowIndex}-${colIndex}`;
          if (clonedSeatMap[key]?.busEntityType === BusEntityType.Seat) {
            clonedSeatMap[key].entityName = `${seatIndex}`.padStart(2, '0') + rowChar;
            seatIndex++;
          }
        }
      }
    } else if (generateType === 4) {
      let seatIndex = startingNumber;
      for (var rowIndex = 0; rowIndex < numberOfRow; rowIndex++) {
        for (var colIndex = 0; colIndex < numberOfCol; colIndex++) {
          let key = `${rowIndex}-${colIndex}`;
          if (clonedSeatMap[key]?.busEntityType === BusEntityType.Seat) {
            let ticketClass = ticketClasses.find(x => x.ticketClassId === clonedSeatMap[key].baseTicketClassId);
            clonedSeatMap[key].entityName = (ticketClass?.name?.at(0)?.toUpperCase() || '') + `${seatIndex}`.padStart(2, '0');
            seatIndex++;
          }
        }
      }
    } else if (generateType === 5) {
      let seatIndex = startingNumber;
      for (var rowIndex = 0; rowIndex < numberOfRow; rowIndex++) {
        for (var colIndex = 0; colIndex < numberOfCol; colIndex++) {
          let key = `${rowIndex}-${colIndex}`;
          if (clonedSeatMap[key]?.busEntityType === BusEntityType.Seat) {
            let ticketClass = ticketClasses.find(x => x.ticketClassId === clonedSeatMap[key].baseTicketClassId);
            clonedSeatMap[key].entityName = `${seatIndex}`.padStart(2, '0') + (ticketClass?.name?.at(0)?.toUpperCase() || '');
            seatIndex++;
          }
        }
      }
    }
    setSeatMap(clonedSeatMap);
  };

  const generateType: number = Form.useWatch('type', generateNameForm);
  return (
    <DndContext
      onDragEnd={e => {
        if (e.active.id && e.over?.id) {
          onMoveEntity(e.active.id as any, e.over.id as any);
        }
      }}
    >
      <div id="bus-design">
        <Card className="bus-card" title={t('seatModelsPage_BusDesign')} size="small">
          <Row gutter={ROW_GUTTER}>
            <Col md={6}>
              <div className="bus-entity">
                <div
                  onMouseDown={() => {
                    setSelectedItems([]);
                  }}
                >
                  {draggableList.map(item => {
                    return <DraggableEntity {...item} itemKey={item.key} key={item.key} />;
                  })}
                </div>
                <Divider />
                <div style={{ textAlign: 'end', marginTop: 10 }}>
                  <Dropdown.Button
                    style={{ justifyContent: 'end' }}
                    type="primary"
                    disabled={!selectedItems?.length}
                    onClick={() => addMultiSeat()}
                    icon={<PlusOutlined />}
                    menu={{
                      items: ticketClasses.map(ticket => {
                        return {
                          label: ticket.name || '',
                          key: ticket.ticketClassId || '',
                        };
                      }),
                      onClick: e => {
                        addMultiSeat(e.key);
                      },
                    }}
                  >
                    {t('seatModelsPage_AddSeats')}
                  </Dropdown.Button>
                  <Button
                    style={{ justifyContent: 'end', marginTop: 10 }}
                    type="primary"
                    onClick={() => setDisplayGenerateSeatNameModal(true)}
                    icon={<ReloadOutlined />}
                  >
                    {t('Generate seat name')}
                  </Button>
                  <Button
                    danger
                    style={{ justifyContent: 'end', marginTop: 10 }}
                    type="primary"
                    onClick={() => setAllEmptySeatAsDefaultValue()}
                    icon={<PlusCircleOutlined />}
                  >
                    {`Set empty seats as ${defaultTicketClass?.name}`}
                  </Button>
                </div>
              </div>
            </Col>

            <Col md={18}>
              <div className="bus-model" id="bus-model" ref={busRef}>
                <div className="bus-header">
                  <BusTopBg />
                </div>
                <div className="bus-container" style={{ backgroundImage: `url(${BusMiddleBg})` }}>
                  <div className="seat-table">
                    {range(0, numberOfRow).map(row => {
                      return (
                        <div className="row" key={row}>
                          {range(0, numberOfCol).map(col => {
                            const key = `${row}-${col}`;
                            let data = seatMap[key];

                            return (
                              <DroppableSeat
                                key={key}
                                seatKey={key}
                                data={data}
                                removeSeat={removeSeat}
                                onChange={(key, seatDetail) => {
                                  setSeatMap(s => {
                                    return { ...s, [key]: seatDetail };
                                  });
                                }}
                                ticketClasses={ticketClasses}
                              />
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="bus-footer">
                  <BusBottomBg />
                </div>
              </div>
            </Col>
          </Row>
        </Card>
        {displayGenerateSeatNameModal ? (
          <Modal
            open={displayGenerateSeatNameModal}
            maskClosable={false}
            width={ModalWidth.medium}
            style={{ top: 10 }}
            className="nta-custom-form"
            title={'Genrate seat name'}
            forceRender={true}
            okText={t('save')}
            cancelText={t('cancel')}
            okButtonProps={{ icon: <SaveOutlined />, className: 'save-btn' }}
            onCancel={() => {
              setDisplayGenerateSeatNameModal(false);
            }}
            onOk={() => {
              generateNameForm
                .validateFields()
                .then(values => {
                  generateSeatName(values.type, values.startingNumber, values.startingCharacter);
                  setDisplayGenerateSeatNameModal(false);
                })
                .catch(info => {});
            }}
          >
            <Form
              initialValues={{
                type: 1,
                startingNumber: 1,
                startingCharacter: 'A',
              }}
              form={generateNameForm}
              size="middle"
              layout="vertical"
            >
              <Row gutter={ROW_GUTTER}>
                <Col md={14}>
                  <Form.Item label={'Select'} name="type" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                    <Radio.Group options={GenerateNameConfig.types}></Radio.Group>
                  </Form.Item>
                </Col>
                <Col md={10}>
                  <Form.Item label={t('Starting number')} name="startingNumber" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                    <InputNumber min={0} precision={0} className="w-100" />
                  </Form.Item>

                  {[2, 3].includes(generateType) ? (
                    <Form.Item label={t('Starting character')} name="startingCharacter" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                      <Select options={GenerateNameConfig.staringChar} />
                    </Form.Item>
                  ) : null}
                </Col>
              </Row>
            </Form>
          </Modal>
        ) : null}
      </div>
    </DndContext>
  );
}
